import { useBooleanFlagValue, useObjectFlagValue } from '@openfeature/react-sdk';

/**
 * Flagsmith JSON config: max chunk uploads in flight per Chronicle bulk-sync
 * pass, keyed by platform — e.g. `{ "desktop": 10 }`. Desktop reads its own
 * field; iOS/Android can slot into the same flag later without a new key. The
 * value is pushed into the bulk-sync engine from the chronicle-bulk-sync
 * provider; the engine floors it at 1.
 */
export const CHUNK_UPLOAD_CONCURRENCY_FLAG_KEY = 'config-chunk-upload-concurrency';

/**
 * Fallback when the flag is unset or malformed. Mirrors the Rust-side
 * `DEFAULT_CHUNKED_UPLOAD_PARALLELISM`; the engine floors whatever it receives at 1.
 */
export const DEFAULT_CHUNK_UPLOAD_CONCURRENCY = 5;

/**
 * Extracts the desktop concurrency from the flag payload, falling back to the
 * default for any missing / non-positive / non-numeric value.
 */
export const parseDesktopChunkUploadConcurrency = (payload: unknown): number => {
  const desktop = (payload as { desktop?: unknown } | null)?.desktop;
  return typeof desktop === 'number' && Number.isFinite(desktop) && desktop >= 1
    ? Math.floor(desktop)
    : DEFAULT_CHUNK_UPLOAD_CONCURRENCY;
};

/**
 * Desktop's max chunk uploads in flight for Chronicle bulk-sync. Re-renders on
 * flag change so a following bulk-sync sweep uses the new value.
 */
export const useDesktopChunkUploadConcurrency = (): number => {
  // getObjectValue returns the payload even when the flag is toggled off, so
  // gate on the enabled state — off (or unset) keeps the built-in default.
  const enabled = useBooleanFlagValue(CHUNK_UPLOAD_CONCURRENCY_FLAG_KEY, false);
  const payload = useObjectFlagValue(CHUNK_UPLOAD_CONCURRENCY_FLAG_KEY, {});
  return enabled
    ? parseDesktopChunkUploadConcurrency(payload)
    : DEFAULT_CHUNK_UPLOAD_CONCURRENCY;
};
